import {
  createRouter,
  createWebHistory,
  createWebHashHistory,
} from 'vue-router';
import { useUserStore } from '@/stores/user';
import {
  isAimetaAndroidWebView,
  isAimetaManagerReachable,
  shouldUseMobileLayout,
  shouldUseWebLoginOnMobile,
} from '@/utils/platform';
import {
  enforceWebLoginFromNative,
  isWebLoginRoutePath,
  syncNativeLoginFromWindow,
} from '@/utils/nativeLoginSync';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'login',
      component: () => import('../views/login/login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/resetPassword',
      name: 'resetPassword',
      component: () => import('../views/login/resetPassword.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/home',
      name: 'home',
      component: () => import('../views/home/index.vue'),
      children: [
        {
          path: '',
          name: 'liveBroadcast',
          component: () => import('../views/home/liveBroadcast.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'roleManage',
          name: 'roleManage',
          component: () => import('../views/home/roleManage.vue'),
          meta: {
            requiresAuth: false,
          },
        },
        {
          path: 'editRole',
          name: 'editRole',
          component: () => import('../views/home/editRole.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'voiceManage',
          name: 'voiceManage',
          component: () => import('../views/home/voiceManage.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'editVoice',
          name: 'editVoice',
          component: () => import('../views/home/editVoice.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'deviceManage',
          name: 'deviceManage',
          component: () => import('../views/home/deviceManage.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'product',
          name: 'product',
          component: () => import('../views/home/StoreProduct.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'editProduct',
          name: 'editProduct',
          component: () => import('../views/home/StoreProductEdit.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'productConfig',
          name: 'productConfig',
          component: () => import('../views/home/StoreProductConfig.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('../views/home/profile.vue'),
          meta: { requiresAuth: false },
        },
        /** 手机底栏知识管理：嵌在 home 壳内，避免独立 /knowledge 整页白屏并污染其它 Tab */
        {
          path: 'knowledge',
          name: 'homeKnowledge',
          component: () => import('../views/knowledge/index.vue'),
          meta: { requiresAuth: false },
        },
      ],
    },
    {
      path: '/liveRoomSetting',
      name: 'liveRoomSetting',
      component: () => import('../views/liveRoom/liveRoomSetting.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/liveRoomSettingMobile',
      name: 'liveRoomSettingMobile',
      component: () => import('../views/liveRoom/liveRoomSettingMobile.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/knowledge',
      name: 'knowledge',
      component: () => import('../views/knowledge/index.vue'),
      meta: { requiresAuth: false, keepAlive: true },
    },
  ],
});

// 全局路由守卫
// const userStore = useUserStore();
// router.beforeEach(async (to) => {
//   let token = localStorage.getItem('token');
//   if (token) {
//     return '/home/';
//   }
//   if (to.meta.requiresAuth && !userStore.token) {
//     return '/';
//   }

//   if (to.path === '/home' && userStore.token) {
//     return '/';
//   }
// });

// 路由前置守卫
router.beforeEach((to, from, next) => {
  if (isAimetaAndroidWebView()) {
    syncNativeLoginFromWindow();
  }

  // 手机 App 且无 AimetaManager：未登录只能停留在 Web 登录页
  if (shouldUseWebLoginOnMobile()) {
    const authed = enforceWebLoginFromNative();
    if (!authed) {
      if (!isWebLoginRoutePath(to.path)) {
        next({ path: '/', replace: true });
        return;
      }
      next();
      return;
    }
    if (to.path === '/') {
      next('/home/');
      return;
    }
    next();
    return;
  }

  // 手机 App：禁止进入独立 /knowledge，统一走 /home/knowledge（避免整页白屏污染其它 Tab）
  // Android App：统一走移动端直播间设置页，避免误进桌面版 /liveRoomSetting
  if (
    shouldUseMobileLayout() &&
    (to.name === 'liveRoomSetting' || to.path === '/liveRoomSetting')
  ) {
    next({
      name: 'liveRoomSettingMobile',
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return;
  }

  if (
    shouldUseMobileLayout() &&
    (to.name === 'knowledge' || to.path === '/knowledge')
  ) {
    next({
      name: 'homeKnowledge',
      query: to.query,
      hash: to.hash,
      replace: true,
    });
    return;
  }
  if (to.path === '/') {
    const isAuthenticated = checkAuth();
    if (!isAuthenticated) {
      // 有 AimetaManager：账号中心由 Android 拉起，Web 勿再调 bridge（避免死循环）
      if (isAimetaManagerReachable()) {
        next('/home/');
        return;
      }
      next();
    } else {
      next('/home/');
    }
  } else if (to.matched.some((record) => record.meta.requiresAuth)) {
    const isAuthenticated = checkAuth();

    if (!isAuthenticated) {
      if (isAimetaManagerReachable()) {
        // 勿 next(false) 会中止导航导致 WebView 全黑
        next({ name: 'liveBroadcast', replace: true });
        return;
      }
      next();
    } else {
      next();
    }
  } else {
    // 不需要认证的路由直接放行
    next();
  }
});

// 检查用户是否登录的函数
function checkAuth() {
  const userStore = useUserStore();
  if (userStore.token) return userStore.token;
  const raw = localStorage.getItem('token');
  if (!raw) return '';
  try {
    return JSON.parse(raw) || '';
  } catch {
    return raw;
  }
}

export default router;
